import { Injectable } from '@angular/core';
import { GenericListEntity } from '@base/entities/BaseEntities';
import { StringMapElement, FieldCustomizationElement, ComponentCustomization } from '@base/classes/BaseClasses';
import { AppInjector } from '../injector/injector.module';
import { Resources_EN } from '../../_resources/EN';
import { Resources_HE } from '../../_resources/HE';
import { Resources_ES } from '../../_resources/ES';
import { SysGlobals } from './sysglobals';
import { AppConfigService } from '@configuration/app.config.service';
import { EnumLanguages } from './sysenums.enum';
import { SystemEndpoint } from '../endpoints/system.endpoint';

@Injectable()
export class ResourcesService {
    private m_Dictionaries: any[];
    private m_CustomDictionaries: any[];
    private m_Lists: any[];
    SelectedLanguages: any[];
    constructor(private appConfigService: AppConfigService,
        private systemEndpoint: SystemEndpoint) { }


    //Prepare dictionaries - we have an hierarchy of 3:
    // Custom dictionary for the specific build
    // General dictionary for the program
    // Base dictionary for the built in "framework" part
    public InitDictionaries() {

        this.m_Dictionaries = [];
        this.m_CustomDictionaries = [];
        this.m_Lists = [];

        switch (SysGlobals.Language) {
            case EnumLanguages.Hebrew:
                {
                    const { Lists, ...Resources } = Resources_HE;
                    this.m_Lists.push(Lists);
                    this.m_Dictionaries.push(Resources);
                    break;
                }
            case EnumLanguages.English:
                {
                    const { Lists, ...Resources } = Resources_EN;
                    this.m_Lists.push(Lists);
                    this.m_Dictionaries.push(Resources);
                    break;
                }
            case EnumLanguages.Spanish:
                {
                    const { Lists, ...Resources } = Resources_ES;
                    this.m_Lists.push(Lists);
                    this.m_Dictionaries.push(Resources);
                    break;
                }
        }

        // add custom resources to the dictionary by current language and custom build
        const allCustomResources = this.appConfigService.CustomResources;
        const customResources = allCustomResources.filter(x => x.Properties && x.Properties.Language === SysGlobals.Language);
        if (customResources && customResources.length) this.m_CustomDictionaries = this.m_CustomDictionaries.concat(customResources);

        // add resources to the dictionary by current language
        const allResources = this.appConfigService.Resources;
        const resources = allResources.filter(x => x.Properties && x.Properties.Language === SysGlobals.Language);
        if (resources && resources.length) this.m_Dictionaries = this.m_Dictionaries.concat(resources);

        // remove undefined to handle cases when some translations do not exist
        this.m_Dictionaries = this.m_Dictionaries.filter(x => x != undefined);

        // add lists to the dictionary by current language
        const allLists = this.appConfigService.AppConfig.GetLists();
        const lists = allLists.filter(x => x.Properties && x.Properties.Language === SysGlobals.Language);
        const overrideLists = lists.filter(x => x.Properties && x.Properties.OverrideModuleName);
        overrideLists.forEach(x => {
            const listsByModuleName = lists.find(z => z.Properties.ModuleName === x.Properties.OverrideModuleName);
            if (listsByModuleName) {
                Object.getOwnPropertyNames(x)
                    .filter(name => name !== 'Properties')
                    .forEach(name => {
                        const sourceList = x[name];
                        listsByModuleName[name] = sourceList;
                    });
            }
        });

        this.m_Lists = this.m_Lists.concat(lists);

        // load field customizations
        SysGlobals.FieldCustomizations = this.LoadFieldCustomizations();
        // load component customizations
        SysGlobals.ComponentCustomizations = this.LoadComponentCustomizations();

        this.systemEndpoint.GetEntitiesResources().subscribe(response => {
            if (!response.IsSuccess) return;

            const entitiesResources = response.Data.filter(x => x.Properties.Language === SysGlobals.Language);
            if (entitiesResources && entitiesResources.length) {
                entitiesResources.forEach(r => {
                    const { Properties, ...entities } = r;
                    const modifiedEntityResources: any = { ...r };
                    Object.keys(modifiedEntityResources).forEach(key => {
                        if (Object.keys(entities).indexOf(key) >= 0) delete modifiedEntityResources[key];
                    });
                    modifiedEntityResources['BL'] = entities;
                    this.m_Dictionaries = this.m_Dictionaries.concat([modifiedEntityResources]);
                })
            }
        });
        //Get selected languages for system
        this.SelectedLanguages = this.GetLanguagesList();
    }


    private GetValueFromPath(obj: any, path: string) {
        return path.split('.').reduce((acc, val) => acc && acc[val], obj);
    }

    //Initializes resources in case they are required before actual initialization took place
    private EnsureInitialized() {
        if (this.m_Dictionaries == undefined) {
            SysGlobals.Language = this.appConfigService.M4UConfig.defaultLanguage;
            this.InitDictionaries();
        }
    }

    //Return resource string for relevant resource
    //If not found,  return the name with $ $
    public RC(sResourceName: string, moduleName?: string, ...args: string[] | number[]): string {
        //Init
        this.EnsureInitialized();

        //Special syntax to allow returning undefined
        var bReturnUndefined: boolean;
        if (sResourceName.startsWith('$$')) {
            sResourceName = sResourceName.substring(2);
            bReturnUndefined = true;
        }

        //Avoid undefined value
        moduleName = moduleName || '';

        //Get resource path
        var sPath = sResourceName.includes('.') ? sResourceName : `Translations.${sResourceName}`;

        let sData = this.getResourceValue(this.m_CustomDictionaries, sPath, moduleName);

        if (sData == undefined) {

            //Get resource value from dictionaries
            sData = this.getResourceValue(this.m_Dictionaries, sPath, moduleName);
        }

        //Return not found with $$ to indicate a problem
        if (sData == undefined) return bReturnUndefined ? undefined : `$${sResourceName}$`;

        //Support formatting arguments
        for (let index = 0; index < args.length; index++) {
            sData = sData.replace(`{${index}}`, args[index]);
        }

        //Support for value replacement
        for (let dictionary of this.m_CustomDictionaries) {
            if (dictionary.StringMap == undefined) continue;
            for (let element of dictionary.StringMap) {
                sData = sData.replace((element as StringMapElement).Old, (element as StringMapElement).New);
            }
        }

        //Return
        return sData;
    }

    getResourceValue(oDictionaries: any[], sPath: string, sModuleName: string) {
        const currentModuleDictionaries = oDictionaries.filter(x => x.Properties && x.Properties.ModuleName === sModuleName);
        //Get value for current module
        let sData = this.tryGetValueFromDictionaries(currentModuleDictionaries, sPath);
        if (sData == undefined) {
            const frameworkDictionaries = oDictionaries.filter(x => !x.Properties || !x.Properties.ModuleName);
            //Process all dictionaries
            //Highest priority dictionary (custom one) is first
            sData = this.tryGetValueFromDictionaries(frameworkDictionaries, sPath);
        }
        return sData;
    }

    tryGetValueFromDictionaries(oDictionaries: any[], sPath: string) {
        let sData: any;
        for (let dictionary of oDictionaries) {
            //Get value, including support for nested values
            sData = this.GetValueFromPath(dictionary, sPath);
            if (sData) break;
        }
        return sData;
    }

    //Return list by name
    public LISTITEM(sListName: string, sValue: string, moduleName?: string): string {
        var list = this.LIST(sListName, moduleName);
        var item = list.find(x => x.Value == sValue);
        if (item == undefined) return `$${sValue}$`;
        return item.Text;
    }

    //Return list by name
    public LISTITEMX(sListName: string, sValue: string, moduleName?: string): GenericListEntity {
        var list = this.LIST(sListName, moduleName);
        var item = list.find(x => x.Value == sValue);
        if (item == undefined) return undefined;
        return item;
    }

    //Return list by name
    public LIST(sListName: string, moduleName?: string): GenericListEntity[] {
        //Init
        this.EnsureInitialized();

        //Avoid undefined value
        moduleName = moduleName || '';

        return this.getResourceValue(this.m_Lists, sListName, moduleName)?.sort(this.sortListfunc) || [];
    }

    // Sort LIST
    sortListfunc(item1: GenericListEntity, item2: GenericListEntity): number {
        // first sort by SortOrder
        if (item1.SortOrder !== undefined && item2.SortOrder === undefined) return -1;
        if (item1.SortOrder === undefined && item2.SortOrder !== undefined) return 1;
        if (item1.SortOrder !== undefined && item2.SortOrder !== undefined) return item1.SortOrder - item2.SortOrder;

        // then sort by Text
        if (item1.Text !== undefined && item2.Text !== undefined) return item1.Text.localeCompare(item2.Text);

        return 0;
    }

    //Load customizations
    public LoadFieldCustomizations(): FieldCustomizationElement[] {

        //Process all dictionaries
        //Highest priority dictionary (custom one) is first
        for (let dictionary of this.m_CustomDictionaries) {
            var oList = this.GetValueFromPath(dictionary, `FieldCustomizations`);
            if (oList != undefined) return oList;
        }

    }

    LoadComponentCustomizations(): ComponentCustomization[] {
        for (let dictionary of this.m_CustomDictionaries) {
            var oList = this.GetValueFromPath(dictionary, `ComponentCustomizations`);
            if (oList != undefined) return oList;
        }
    }

    //Build languages list
    public GetLanguagesList(): GenericListEntity[] { 
        const listLang = this.LIST("Languages");    
        var list = [];
        const languages = this.appConfigService.M4UConfig.languages;
        //if list not defined then return all languges
        if (!languages?.length) return listLang;
 
        listLang.forEach(item => {
            if (languages.includes(item.Value)) {
                list.push(item);
            }
        });

        return list;
    }
    public IsSelectedLanguageLoaded(lang: string): boolean {
        this.EnsureInitialized();
        return AppConfigService.GetLoadedLanguages().includes(lang);
    }
}


export function RC(value: string, ...args: string[] | number[]) {
    return AppInjector.get(ResourcesService).RC(value, '', ...args);
}


export function LIST(sListName: string): GenericListEntity[] {
    return AppInjector.get(ResourcesService).LIST(sListName);
}


export function LISTITEM(sListName: string, sValue: string): string {
    return AppInjector.get(ResourcesService).LISTITEM(sListName, sValue);
}

export function LISTITEMX(sListName: string, sValue: string): GenericListEntity {
    return AppInjector.get(ResourcesService).LISTITEMX(sListName, sValue);
}

